import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { useProducts, Product } from "@/hooks/useProducts";
import ProductCard from "./ProductCard";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";

const AIRecommendations = () => {
  const { user } = useAuth();
  const { data: products = [] } = useProducts();
  const [recommended, setRecommended] = useState<Product[]>([]);
  const [reasoning, setReasoning] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    if (!user || fetched || products.length === 0) return;

    const fetchRecommendations = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke("ai-recommendations");

        if (error) {
          console.error("Recommendations error:", error);
          return;
        }

        if (data?.recommendations) {
          const recs = data.recommendations
            .map((id: string) => products.find((p) => p.id === id))
            .filter(Boolean) as Product[];
          setRecommended(recs);
          setReasoning(data.reasoning || "");
        }
      } catch (err) {
        console.error("Failed to fetch recommendations:", err);
      } finally {
        setLoading(false);
        setFetched(true);
      }
    };

    fetchRecommendations();
  }, [user, fetched, products]);

  if (!user || (fetched && recommended.length === 0)) return null;

  return (
    <section className="py-20">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-10"
        >
          <div className="flex items-center gap-2">
            <div className="rounded-lg bg-primary/10 p-2">
              <Sparkles className="h-5 w-5 text-primary" />
            </div>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary">AI Picks</p>
          </div>
          <h2 className="mt-3 font-display text-3xl font-black sm:text-4xl">Recommended For You</h2>
          {reasoning && (
            <p className="mt-2 text-sm text-muted-foreground">{reasoning}</p>
          )}
        </motion.div>

        {loading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="animate-pulse rounded-2xl border border-border/50 bg-card">
                <div className="aspect-square bg-secondary rounded-t-2xl" />
                <div className="p-4 space-y-3">
                  <div className="h-4 w-2/3 rounded bg-secondary" />
                  <div className="h-4 w-1/3 rounded bg-secondary" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {recommended.map((product, i) => (
              <ProductCard key={product.id} product={product} index={i} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default AIRecommendations;
